import React, { FC, useEffect, useState, useMemo } from 'react';
import _ from 'lodash';
import {
  Category,
  Service,
} from '@wix/ambassador-marketplace-categories-v1-category/types';
import { exploreResultsPageGenericClick } from '@wix/bi-logger-marketplace/v2';
import {
  ACTIONS,
  useExploreContext,
} from '@components/Contexts/ExploreContext';
import { useBiSessionContext } from '@components/Contexts/BiSessionContext';
import { updateQueryParams, ClearAll } from '@utils/route';
import { useBi } from '@components/Hooks/useBi';
import { useShallowRouter } from '@components/Hooks/useRouter';
import MobileServices from '../../MobileFilters/MobileServices';
import DesktopServices from './DesktopServices';

type ServiceWithSelection = Service & {
  isSelected: boolean;
};

type CategoryWithSelection = Category & {
  isSelected: boolean;
  servicePaths: Record<string, ServiceWithSelection>;
};

type Props = {
  onBack?: () => void;
  onClose?: () => void;
  closeModal?: () => void;
  isMobile?: boolean;
  clearWasClicked?: ClearAll;
  setClearWasClicked?: (x: ClearAll) => void;
};

const Services: FC<Props> = ({
  onBack,
  onClose,
  closeModal,
  isMobile,
  clearWasClicked,
  setClearWasClicked,
}) => {
  const { sessionId } = useBiSessionContext();
  const {
    dispatch,
    state: { services, selectedServices, entry, origin, currentPage },
  } = useExploreContext();

  const logger = useBi();
  const { navigate, router } = useShallowRouter();

  const [selection, setSelection] = useState<
    Record<string, CategoryWithSelection>
  >({});

  const hasBeenCleared = useMemo(() => {
    return clearWasClicked?.all || clearWasClicked?.services;
  }, [clearWasClicked?.all, clearWasClicked?.services]);

  useEffect(() => {
    const _selection: Record<string, CategoryWithSelection> = {};
    _.forEach(
      _.cloneDeep(services) as Array<CategoryWithSelection>,
      (category) => {
        const _services: Record<string, ServiceWithSelection> = {};
        let selectCategory = true;
        _.forEach(category.services, (s) => {
          const serviceIsSelected = !_.isUndefined(
            _.find(selectedServices, { id: s.id }),
          );

          selectCategory = selectCategory && serviceIsSelected;

          if (hasBeenCleared) {
            _services[s.displayKey!] = {
              ...s,
              isSelected: false,
            };
          } else {
            _services[s.displayKey!] = {
              ...s,
              isSelected: serviceIsSelected,
            };
          }
        });
        category.servicePaths = _services;
        if (hasBeenCleared) {
          category.isSelected = false;
        } else {
          category.isSelected = selectCategory;
        }
        _selection[category.displayKey!] = category;
      },
    );

    setSelection(_selection);
  }, [services, selectedServices, hasBeenCleared]);

  const hasSelectionData = useMemo(() => {
    return _.size(selection) > 0;
  }, [selection]);

  const numSelectedServices = useMemo(() => {
    let count = 0;
    _.forEach(selection, (category) => {
      count += _.size(
        _.filter(category.servicePaths, (service) => service.isSelected),
      );
      return count;
    });
    return count;
  }, [selection]);

  const selectAService = (service: Service, category: string) => {
    if (hasSelectionData) {
      const _selection = _.cloneDeep(selection);
      const serviceToUpdate =
        _selection?.[category]?.servicePaths[service.displayKey!];
      if (!_.isUndefined(serviceToUpdate)) {
        serviceToUpdate.isSelected = !serviceToUpdate.isSelected;
      }

      const categoryWithChange = _selection?.[category];
      const isCategorySelected =
        _.size(
          _.filter(
            categoryWithChange?.servicePaths,
            (service) => service?.isSelected === false,
          ),
        ) === 0;
      _selection[category].isSelected = isCategorySelected;
      setSelection(_selection);
    }
  };

  const selectCategory = (category: string) => {
    if (hasSelectionData) {
      const _selection = _.cloneDeep(selection);
      const categoryIsSelected = selection[category]?.isSelected ?? false;
      _.forEach(_selection[category]?.servicePaths ?? [], (s) => {
        s.isSelected = !categoryIsSelected;
      });
      if (!_.isUndefined(_selection[category])) {
        _selection[category].isSelected = !categoryIsSelected;
      }
      setSelection(_selection);
    }
  };

  const isServiceSelected = (service: Service, category: string) => {
    if (hasSelectionData) {
      return (
        selection[category]?.servicePaths[service.displayKey!]?.isSelected ??
        false
      );
    }
  };

  const isCategorySelected = (category: string) => {
    if (hasSelectionData) {
      return selection[category]?.isSelected ?? false;
    }
  };

  const onClear = () => {
    setClearWasClicked &&
      setClearWasClicked({ ...clearWasClicked!, services: true });
    const _selection = _.cloneDeep(selection);
    _.forEach(_selection, (category) => {
      category.isSelected = false;
      _.forEach(_selection[category.displayKey!].servicePaths, (s) => {
        s.isSelected = false;
      });
    });
    setSelection(_selection);

    logger.report(
      exploreResultsPageGenericClick({
        clickType: 'clear all services filters',
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
        data: JSON.stringify({
          currentPage,
        }),
      }),
    );
  };

  const onApply = () => {
    const serviceDisplayKeys: Array<string> = [];
    _.map(selection, (category) => {
      return _.map(category.servicePaths, (s) => {
        if (s.isSelected) {
          return serviceDisplayKeys.push(s.displayKey!);
        }
      });
    });

    const serviceIds = serviceDisplayKeys.join(',');

    const params = updateQueryParams(
      {
        serviceIds,
      },
      router,
      clearWasClicked,
    );

    navigate(params, false);

    dispatch({
      type: ACTIONS.FILTER_POPOVER_STATE,
      shouldOpen: false,
    });

    logger.report(
      exploreResultsPageGenericClick({
        clickType: 'apply service filter',
        sessionId,
        origin,
        entry,
        exploreVersion: 'V2',
        data: JSON.stringify({
          servicesSelected: serviceIds,
          currentPage,
        }),
      }),
    );
  };

  if (isMobile) {
    return (
      <MobileServices
        selectAService={selectAService}
        isServiceSelected={isServiceSelected}
        isCategorySelected={isCategorySelected}
        hasSelectionData={hasSelectionData}
        selection={selection}
        selectCategory={selectCategory}
        onApply={onApply}
        onClear={onClear}
        onBack={onBack}
        onClose={onClose}
        closeModal={closeModal}
        numSelectedServices={numSelectedServices}
      />
    );
  }

  return (
    <DesktopServices
      selectAService={selectAService}
      isServiceSelected={isServiceSelected}
      isCategorySelected={isCategorySelected}
      hasSelectionData={hasSelectionData}
      selection={selection}
      selectCategory={selectCategory}
      onApply={onApply}
      onClear={onClear}
      numSelectedServices={numSelectedServices}
    />
  );
};

export default Services;
